import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AdminUser } from '@digitaltattoo/types';
import { clearStoredCurrentShopId, readUserIdFromToken } from '../../utils/currentShop';

/** Backoffice auth user: AdminUser plus optional fields for ADMIN/PRACTITIONER UI. */
export type BackofficeAuthUser = AdminUser & {
  userType?: 'ADMIN' | 'PRACTITIONER';
  companyId?: string;
  permissions?: {
    screenSaverLock?: boolean;
    [key: string]: unknown;
  } | null;
  firstName?: string;
  lastName?: string;
  phone?: string;
  emailVerified?: boolean;
};

interface AuthState {
  isAuthenticated: boolean;
  user: BackofficeAuthUser | null;
  token: string | null;
  refreshToken: string | null;
  loading: boolean;
  error: string | null;
}

// Check if user is already authenticated on app start
const token = localStorage.getItem('token');
const refreshToken = localStorage.getItem('refresh_token');
const userStr = localStorage.getItem('user');
const user = userStr ? JSON.parse(userStr) : null;

const initialState: AuthState = {
  isAuthenticated: !!token && !!user,
  user: user,
  token: token,
  refreshToken,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<{ user: BackofficeAuthUser; token: string; refreshToken?: string }>) => {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken || null;
      state.loading = false;
      state.error = null;
      
      // Persist to localStorage
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('user', JSON.stringify(action.payload.user));
      if (action.payload.refreshToken) {
        localStorage.setItem('refresh_token', action.payload.refreshToken);
      } else {
        localStorage.removeItem('refresh_token');
      }
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.loading = false;
      state.error = action.payload;
      
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.loading = false;
      state.error = null;

      // Drop the tab's shop selection while the token can still identify the
      // user, so the next login starts from the role default scope.
      const shopScopeUserId = readUserIdFromToken();
      if (shopScopeUserId) clearStoredCurrentShopId(shopScopeUserId);

      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    },
    clearError: (state) => {
      state.error = null;
    },
    updateUserProfile: (state, action: PayloadAction<{ name?: string; email?: string }>) => {
      if (state.user) {
        state.user = {
          ...state.user,
          ...(action.payload.name && { name: action.payload.name }),
          ...(action.payload.email && { email: action.payload.email }),
        };
        // Update localStorage
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },
  },
});

export const { loginStart, loginSuccess, loginFailure, logout, clearError, updateUserProfile } = authSlice.actions;
export default authSlice.reducer;